import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../supabase/supabaseClient";
import AvatarEditor from "./AvatarEditor";
import { getOrgForUser, getMembers, updateOrg, uploadOrgAvatar } from "../services/orgService";
import { removeMember } from "../services/membershipService";
import { deletePostWithMediaCleanup } from "../services/postsService";
import "./OrgDashboard.css";

const AVATAR_ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const AVATAR_MAX_SIZE_BYTES = 5 * 1024 * 1024;

export default function OrgDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [role, setRole] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [members, setMembers] = useState([]);
  const [orgAvatarUrl, setOrgAvatarUrl] = useState("");
  const [orgAvatarFailed, setOrgAvatarFailed] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [pendingOrgAvatarFile, setPendingOrgAvatarFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [orgPosts, setOrgPosts] = useState([]);
  const [deletingOrgPostId, setDeletingOrgPostId] = useState("");
  const [openOrgPostMenuId, setOpenOrgPostMenuId] = useState("");
  const [isEditingOrg, setIsEditingOrg] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);
  const [editOrgName, setEditOrgName] = useState("");
  const [editOrgType, setEditOrgType] = useState("");
  const [editOrgCity, setEditOrgCity] = useState("");
  const [editOrgWebsite, setEditOrgWebsite] = useState("");
  const [editOrgBio, setEditOrgBio] = useState("");
  const orgAvatarInputRef = useRef(null);

  const stripOrgPostMarker = (body) => {
    if (typeof body !== "string") return "";
    return body.replace(/\n\n\[posted_as_org:[^\]]+\]\s*$/i, "").trimEnd();
  };
  
  const loadData = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.auth.getSession();
    const currentUser = data?.session?.user ?? null;
    setUser(currentUser);
    
    if (!currentUser) {
      navigate("/login");
      return;
    }
    
    const { data: orgData, error: orgErr } = await getOrgForUser(currentUser.id);
    console.log("getOrgForUser result:", { orgData, orgErr });
    if (orgErr) {
      console.error("Error fetching org for user:", orgErr);
      setStatus("Could not load organization (permission error)");
      setLoading(false);
      return;
    }

    if (!orgData || !orgData.org) {
      // No membership or org found for this user
      navigate("/onboarding/org");
      return;
    }

    setOrg(orgData.org);
  setOrgAvatarUrl(orgData.org?.avatar_url || "");
    setOrgAvatarFailed(false);
    setEditOrgName(orgData.org?.name || "");
    setEditOrgType(orgData.org?.type || "");
    setEditOrgCity(orgData.org?.city || "");
    setEditOrgWebsite(orgData.org?.website || "");
    setEditOrgBio(orgData.org?.bio || "");
    const normalizedRole = orgData.role ? orgData.role.toString().toLowerCase() : null;
    setRole(normalizedRole);
    setUserRole(normalizedRole);
    console.log("org role:", normalizedRole, "membership:", orgData.membership);
    
    // Load members
    const { data: membersData, error: membersError } = await getMembers(orgData.org.id);
    if (membersError) {
      console.error("Error loading members:", membersError);
      setStatus("Could not load organization members (permission error)");
      setLoading(false);
      return;
    }

    if (membersData) {
      // Hydrate member names
      const hydrated = await Promise.all(
        membersData.map(async (m) => {
          const { data: profile, error: profileError } = await supabase
          .from("user_profiles")
          .select("first_name, last_name")
          .eq("user_id", m.user_id)
          .maybeSingle();

          if (profileError) {
            console.error("Error loading member profile:", profileError);
          }

          return {
            ...m,
            name: profile
            ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim()
            : m.user_id === currentUser.id
              ? "You"
              : "Member",
          };
        })
      );
      setMembers(hydrated);
    }

    const marker = `[posted_as_org:${orgData.org.id}]`;
    const { data: postRows, error: postsError } = await supabase
      .from("posts")
      .select("id, author_id, body, created_at, post_media(id, media_type, url, position)")
      .ilike("body", `%${marker}%`)
      .is("deleted_at", null)
      .order("created_at", { ascending: false })
      .limit(12);

    if (postsError) {
      console.error("Error loading org posts:", postsError);
      setStatus((prev) => prev || "Could not load organization posts.");
      setOrgPosts([]);
    } else {
      const authorIds = Array.from(new Set((postRows || []).map((post) => post.author_id).filter(Boolean)));
      const authorMap = {};

      if (authorIds.length > 0) {
        const { data: userProfiles } = await supabase
          .from("user_profiles")
          .select("user_id, first_name, last_name")
          .in("user_id", authorIds);

        (userProfiles || []).forEach((profile) => {
          const fullName = `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
          if (fullName) authorMap[profile.user_id] = fullName;
        });

        const unresolvedIds = authorIds.filter((id) => !authorMap[id]);
        if (unresolvedIds.length > 0) {
          const { data: playerProfiles } = await supabase
            .from("player_profiles")
            .select("user_id, firstname, lastname")
            .in("user_id", unresolvedIds);

          (playerProfiles || []).forEach((profile) => {
            const fullName = `${profile.firstname || ""} ${profile.lastname || ""}`.trim();
            if (fullName) authorMap[profile.user_id] = fullName;
          });
        }
      }

      setOrgPosts((postRows || []).map((post) => ({
        id: post.id,
        authorId: post.author_id,
        authorName: authorMap[post.author_id] || "Member",
        body: stripOrgPostMarker(post.body || ""),
        createdAt: post.created_at,
        media: Array.isArray(post.post_media)
          ? [...post.post_media]
              .sort((a, b) => (a.position || 0) - (b.position || 0))
              .map((item) => ({ type: item.media_type, url: item.url }))
          : [],
      })));
    }
    
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  
  const handleRemove = async (memberUserId) => {
    if (!org) return;
    if (!window.confirm("Remove this member from the organization?")) return;
    setStatus("Removing member...");
    const { error } = await removeMember(org.id, memberUserId);
    if (error) {
      setStatus(`Remove failed: ${error.message}`);
      return;
    }
    setStatus("Member removed.");
    loadData();
  };

  const handleCancelEditOrg = () => {
    if (!org) return;
    setEditOrgName(org.name || "");
    setEditOrgType(org.type || "");
    setEditOrgCity(org.city || "");
    setEditOrgWebsite(org.website || "");
    setEditOrgBio(org.bio || "");
    setPendingOrgAvatarFile(null);
    setIsEditingOrg(false);
    setStatus("");
  };

  const handleDeleteOrgPost = async (postId) => {
    const targetPost = orgPosts.find((post) => post.id === postId);
    if (!targetPost || deletingOrgPostId) return;
    if (!user?.id || targetPost.authorId !== user.id) {
      setStatus("You can only delete posts you authored.");
      return;
    }
    if (!window.confirm("Delete this post? This cannot be undone.")) return;

    setDeletingOrgPostId(postId);
    setOpenOrgPostMenuId("");
    setStatus("Deleting post...");
    try {
      const { error, cleanup } = await deletePostWithMediaCleanup(postId, targetPost.media || []);
      if (error) {
        setStatus(`Failed to delete post: ${error.message}`);
        return;
      }

      setOrgPosts((prev) => prev.filter((post) => post.id !== postId));
      if (cleanup?.failures?.length) {
        setStatus("Post deleted, but some media files could not be removed from storage.");
      } else {
        setStatus("Post deleted.");
      }
    } catch (error) {
      console.error("Error deleting organization post:", error);
      setStatus("Failed to delete post.");
    } finally {
      setDeletingOrgPostId("");
    }
  };

  const handleOrgAvatarChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!AVATAR_ALLOWED_TYPES.includes(file.type)) {
      setStatus("Please choose a JPG, PNG, or WebP image.");
      event.target.value = "";
      return;
    }

    if (file.size > AVATAR_MAX_SIZE_BYTES) {
      setStatus("Image must be 5 MB or smaller.");
      event.target.value = "";
      return;
    }

    setStatus("");
    setPendingOrgAvatarFile(file);
    event.target.value = "";
  };

  const handleOpenOrgAvatarPicker = () => {
    if (avatarUploading || savingOrg) return;
    if (orgAvatarInputRef.current) {
      orgAvatarInputRef.current.value = "";
      orgAvatarInputRef.current.click();
    }
  };

  const handleOrgAvatarApply = async (croppedFile) => {
    if (!org) {
      setPendingOrgAvatarFile(null);
      return;
    }

    setPendingOrgAvatarFile(null);
    setAvatarUploading(true);
    setStatus("Uploading organization photo...");

    const { data: avatarData, error: avatarError } = await uploadOrgAvatar(org.id, croppedFile);
    if (avatarError) {
      setStatus(`Organization photo upload failed: ${avatarError.message}`);
      setAvatarUploading(false);
      return;
    }

    const nextAvatarUrl = avatarData?.avatar_url || "";
    setOrg((prev) => (prev ? { ...prev, avatar_url: nextAvatarUrl } : prev));
    setOrgAvatarUrl(nextAvatarUrl);
    setOrgAvatarFailed(false);
    setAvatarUploading(false);
    setStatus("Organization photo updated.");
  };

  const handleSaveOrg = async (event) => {
    event.preventDefault();
    if (!org || role !== "admin" || savingOrg) return;

    setSavingOrg(true);
    setStatus("Saving organization profile...");

    const payload = {
      name: editOrgName.trim() || null,
      type: editOrgType.trim() || null,
      city: editOrgCity.trim() || null,
      website: editOrgWebsite.trim() || null,
      bio: editOrgBio.trim() || null,
    };

    const { data, error } = await updateOrg(org.id, payload);
    if (error || !data) {
      setStatus(`Failed to save organization profile: ${error?.message || "Unknown error"}`);
      setSavingOrg(false);
      return;
    }

    setOrg(data);

    setEditOrgName(data.name || "");
    setEditOrgType(data.type || "");
    setEditOrgCity(data.city || "");
    setEditOrgWebsite(data.website || "");
    setEditOrgBio(data.bio || "");
    setIsEditingOrg(false);
    setSavingOrg(false);
    setStatus("Organization profile updated.");
  };
  
  if (loading) {
    return (
      <main className="page">
        <section className="card org-profile org-profile-skeleton">
          <div className="profile-cover org-skeleton-shimmer" />
          <div className="profile-grid">
            <div className="profile-main">
              <div className="profile-header">
                <div className="avatar-circle large org-skeleton-shimmer" />
                <div className="org-skeleton-header-lines">
                  <div className="org-skeleton-line org-skeleton-line-title org-skeleton-shimmer" />
                  <div className="org-skeleton-line org-skeleton-line-subtitle org-skeleton-shimmer" />
                </div>
              </div>

              <div className="section org-skeleton-card">
                <div className="org-skeleton-line org-skeleton-line-section org-skeleton-shimmer" />
                <div className="org-skeleton-line org-skeleton-line-full org-skeleton-shimmer" />
                <div className="org-skeleton-line org-skeleton-line-full org-skeleton-shimmer" />
                <div className="org-skeleton-line org-skeleton-line-half org-skeleton-shimmer" />
              </div>

              <div className="section org-skeleton-card">
                <div className="org-skeleton-line org-skeleton-line-section org-skeleton-shimmer" />
                <div className="org-skeleton-line org-skeleton-line-full org-skeleton-shimmer" />
              </div>
            </div>

            <aside className="profile-sidebar">
              <div className="sidebar-card org-skeleton-card">
                <div className="org-skeleton-line org-skeleton-line-section org-skeleton-shimmer" />
                <div className="org-skeleton-line org-skeleton-line-full org-skeleton-shimmer" />
                <div className="org-skeleton-line org-skeleton-line-half org-skeleton-shimmer" />
              </div>

              <div className="sidebar-card org-skeleton-card">
                <div className="org-skeleton-line org-skeleton-line-section org-skeleton-shimmer" />
                <div className="org-skeleton-line org-skeleton-line-full org-skeleton-shimmer" />
                <div className="org-skeleton-line org-skeleton-line-full org-skeleton-shimmer" />
                <div className="org-skeleton-line org-skeleton-line-full org-skeleton-shimmer" />
              </div>
            </aside>
          </div>
        </section>
      </main>
    );
  }
  
  return (
    <>
    {pendingOrgAvatarFile && (
      <AvatarEditor
        file={pendingOrgAvatarFile}
        onApply={handleOrgAvatarApply}
        onCancel={() => setPendingOrgAvatarFile(null)}
      />
    )}
    <main className="page">
      <section className="card org-profile">
        <div className="profile-cover" />

        <div className="profile-grid">
          <div className="profile-main">
            <div className="profile-header">
              {orgAvatarUrl && !orgAvatarFailed ? (
                <img
                  src={orgAvatarUrl}
                  alt={org.name}
                  className="avatar-circle large org-avatar-image"
                  onError={() => setOrgAvatarFailed(true)}
                />
              ) : (
                <div className="avatar-circle large">{(org.name || "").charAt(0)}</div>
              )}
              <div>
                <h1 className="org-name">{org.name}</h1>
                <div className="org-meta">
                  {org.verified && <span className="verified-badge">✓ Verified</span>}
                </div>
                <p className="org-subtitle">{org.type || "Organization"} • {org.city || ""}</p>
                <Link to={`/org/${org.id}`} className="org-public-link">View Public Profile</Link>
              </div>
              {role === "admin" && !isEditingOrg && (
                <button className="btn-primary-small" type="button" onClick={() => setIsEditingOrg(true)}>
                  Edit Organization
                </button>
              )}
            </div>

            {role === "admin" && isEditingOrg ? (
              <div className="section">
                <h2>Edit Organization</h2>
                <form className="org-edit-form" onSubmit={handleSaveOrg}>
                  <label>
                    Organization Profile Picture
                    <div className="org-avatar-edit-row">
                      {orgAvatarUrl && !orgAvatarFailed ? (
                        <div className="org-avatar-edit-preview">Current image ready</div>
                      ) : (
                        <div className="org-avatar-edit-preview">No organization picture uploaded yet</div>
                      )}
                      <div className="org-avatar-edit-actions">
                        <button
                          type="button"
                          className="btn-secondary-small"
                          onClick={handleOpenOrgAvatarPicker}
                          disabled={savingOrg || avatarUploading}
                        >
                          {orgAvatarUrl && !orgAvatarFailed ? "Change Picture" : "Upload Picture"}
                        </button>
                        {avatarUploading && <span className="muted">Uploading...</span>}
                      </div>
                      <input
                        ref={orgAvatarInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="org-avatar-file-input"
                        onChange={handleOrgAvatarChange}
                      />
                    </div>
                  </label>
                  <label>
                    Name
                    <input
                      type="text"
                      value={editOrgName}
                      onChange={(e) => setEditOrgName(e.target.value)}
                      required
                    />
                  </label>
                  <label>
                    Type
                    <input
                      type="text"
                      value={editOrgType}
                      onChange={(e) => setEditOrgType(e.target.value)}
                      placeholder="Club, Academy, Agency..."
                    />
                  </label>
                  <label>
                    City
                    <input
                      type="text"
                      value={editOrgCity}
                      onChange={(e) => setEditOrgCity(e.target.value)}
                    />
                  </label>
                  <label>
                    Website
                    <input
                      type="url"
                      value={editOrgWebsite}
                      onChange={(e) => setEditOrgWebsite(e.target.value)}
                      placeholder="https://example.com"
                    />
                  </label>
                  <label>
                    Bio
                    <textarea
                      rows={4}
                      value={editOrgBio}
                      onChange={(e) => setEditOrgBio(e.target.value)}
                    />
                  </label>
                  <div className="org-edit-actions">
                    <button type="button" className="btn-secondary-small" onClick={handleCancelEditOrg} disabled={savingOrg}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary-small" disabled={savingOrg || !editOrgName.trim()}>
                      {savingOrg ? "Saving..." : "Save"}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="section org-overview-card">
                <h2>Overview</h2>
                <p className="org-overview-text">{org.bio || "No description yet."}</p>
                  <div className="org-dashboard-stat-row">
                    <div className="org-dashboard-stat-card">
                      <span className="org-dashboard-stat-label">Public members</span>
                      <strong>{members.length}</strong>
                    </div>
                    <div className="org-dashboard-stat-card">
                      <span className="org-dashboard-stat-label">Org posts</span>
                      <strong>{orgPosts.length}</strong>
                    </div>
                    <div className="org-dashboard-stat-card">
                      <span className="org-dashboard-stat-label">Visibility</span>
                      <strong>Public</strong>
                    </div>
                  </div>
              </div>
            )}

            {/* <div className="section">
                <div className="org-section-header-row">
                  <h2>Public Profile</h2>
                  <Link to={`/org/${org.id}`} className="org-inline-link">Open public view</Link>
                </div>
                <div className="org-dashboard-public-card">
                  <p className="muted">
                    Your organization now has a visitable public profile. Members and org-authored posts surface there automatically.
                  </p>
                  <div className="org-dashboard-public-actions">
                    <Link to={`/org/${org.id}`} className="btn-primary-small">Preview Public Profile</Link>
                    {role === "admin" && !isEditingOrg && (
                      <button className="btn-secondary-small" type="button" onClick={() => setIsEditingOrg(true)}>
                        Edit Public Details
                      </button>
                    )}
                  </div>
              </div>
            </div> */}

            <div className="section">
                <div className="org-section-header-row">
                  <h2>Organization Posts</h2>
                  <span className="muted">Posted as {org.name}</span>
                </div>
                <div className="org-dashboard-posts-list">
                  {orgPosts.length === 0 ? (
                    <p className="muted">No organization-authored posts yet. Use Home and choose Post as: {org.name}.</p>
                  ) : (
                    orgPosts.map((post) => (
                      <article key={post.id} className="org-dashboard-post-card">
                        <div className="org-dashboard-post-meta">
                          <div>
                            <span className="org-dashboard-post-label">Posted by </span>
                            <Link to={`/player/${post.authorId}`} className="org-inline-link">{post.authorName}</Link>
                          </div>
                          <div className="org-dashboard-post-meta-right">
                            <span className="muted">
                              {new Date(post.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                            </span>
                            {user?.id === post.authorId && (
                              <div className="org-post-menu-wrap">
                                <button
                                  className="org-post-menu-trigger"
                                  type="button"
                                  aria-label="Post actions"
                                  aria-expanded={openOrgPostMenuId === post.id}
                                  onClick={() => setOpenOrgPostMenuId((prev) => (prev === post.id ? "" : post.id))}
                                  disabled={deletingOrgPostId === post.id}
                                >
                                  ...
                                </button>
                                {openOrgPostMenuId === post.id && (
                                  <div className="org-post-menu-dropdown">
                                    <button
                                      className="org-post-menu-item org-post-menu-item-danger"
                                      type="button"
                                      onClick={() => handleDeleteOrgPost(post.id)}
                                      disabled={deletingOrgPostId === post.id}
                                    >
                                      {deletingOrgPostId === post.id ? "Deleting..." : "Delete"}
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="org-dashboard-post-body">{post.body || "(No text content)"}</p>
                        {post.media.length > 0 && (
                          <div className={`org-dashboard-post-media ${post.media.length > 1 ? "org-dashboard-post-media-grid" : ""}`}>
                            {post.media.map((item, index) => (
                              item.type === "video"
                                ? <video key={`${post.id}-${index}`} src={item.url} controls preload="metadata" />
                                : <img key={`${post.id}-${index}`} src={item.url} alt="Organization post media" />
                            ))}
                          </div>
                        )}
                        <div className="org-dashboard-post-actions">
                          <Link
                            to="/home"
                            state={{
                              focusPostId: post.id,
                              focusPostPreview: {
                                id: post.id,
                                author: org.name,
                                authorId: user?.id || null,
                                orgId: org.id,
                                body: post.body,
                                media: post.media,
                                created_at: post.createdAt,
                                authorHeadline: `${org.type || "Organization"}${org.city ? ` · ${org.city}` : ""}`,
                              }
                            }}
                            className="org-inline-link"
                          >
                            View in Home
                          </Link>
                        </div>
                      </article>
                    ))
                  )}
              </div>
            </div>
          </div>

          <aside className="profile-sidebar">
            <div className="sidebar-card org-about-compact">
              <h3>About</h3>
              <div className="org-about-grid">
                <div className="org-about-item">
                  <span className="org-about-label">Website</span>
                  <span className="org-about-value">
                    {org.website ? <a href={org.website} target="_blank" rel="noopener noreferrer">{org.website}</a> : "—"}
                  </span>
                </div>
                <div className="org-about-item">
                  <span className="org-about-label">Verified</span>
                  <span className="org-about-value">{org.verified ? "Yes ✓" : "No (pending)"}</span>
                </div>
                <div className="org-about-item">
                  <span className="org-about-label">Type</span>
                  <span className="org-about-value">{org.type || "Organization"}</span>
                </div>
                <div className="org-about-item">
                  <span className="org-about-label">City</span>
                  <span className="org-about-value">{org.city || "—"}</span>
                </div>
              </div>
            </div>

            <div className="sidebar-card">
              <div className="sidebar-card-header">
                <h3>Members ({members.length})</h3>
                {userRole && (
                  <span className={`role-badge role-badge-${userRole}`}>{userRole.toString().toUpperCase()}</span>
                )}
              </div>
              <div className="members-list">
                {members.map((m) => (
                  <div key={m.user_id} className="member-row">
                    <div className="member-info">
                      <Link to={`/player/${m.user_id}`} className="member-name member-name-link">{m.name}</Link>
                      <span className="member-role">{m.orgrole || "member"}</span>
                      {m.user_id === user.id && <span className="member-own">(You)</span>}
                    </div>
                    {role === "admin" && m.user_id !== user.id && (
                      <button className="btn-danger-small" onClick={() => handleRemove(m.user_id)}>Remove</button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {status && <p className="status-text">{status}</p>}
          </aside>
        </div>
      </section>
    </main>
    </>
  );
}
